/*
 * Glitch lead events for GA4 and Google Ads, 6 Oct 2026.
 * Pushes to window.dataLayer, and to gtag when it exists. Never sends a page view:
 * the app-loaded Google tag owns page views. The only event that fires on page load is
 * the native forms' thank-you view count below.
 *
 * What fires each event:
 * lead_call: a click on any tel: link.
 * lead_email: a click on any mailto: link.
 * lead_registration_start: a click on a link whose href contains "regist" (anywhere on the
 *   page), or on a link to /pages/become-a-distributor-or-reseller that sits outside the
 *   header, nav and footer (the menu's Distributors link is a visit, not an apply click).
 *   Delegated, since SparkLayer injects some of these links after load.
 * lead_quote, lead_government, lead_registration, lead_contact (native forms): the load of
 *   the thank-you view, the page holding an element with data-glitch-thanks. Its value picks
 *   the event and is sent as form_id; an unknown value sends lead_form. Once per page load,
 *   and not on a reload or a back/forward visit, so a revisit is not a second send.
 *   Why the thank-you view and not the submit: gtag sends events in a batch a moment later,
 *   and the form post leaves the page first, so a count made at submit never reached GA4.
 *   The thank-you view loads only after Shopify accepted the send and stays put.
 *   The thank-you count waits for the Google tag, which Shopify defines just after the page
 *   finishes parsing.
 *   A thank-you view counts only after a send from the same tab within 30 minutes, so a
 *   direct visit to the thank-you address never counts.
 *   The submit of a form holding a hidden glitch_form input fires nothing, and never the
 *   generic contact event; it only leaves that same-tab flag in sessionStorage.
 * lead_contact (unmarked Shopify contact form): the submit of a form with form_type=contact,
 *   form_id shopify-contact.
 * Jotform leads (registration, W-9 and reseller pages, and any rollback): the forms are
 *   iframes, so the page never sees their submit. The frame posts
 *   { action: 'submission-completed', formID } to the parent on submit, as an object or a
 *   JSON string. The form id picks the event name; an unknown id sends lead_form.
 */
(function () {
  'use strict';

  // Form ids from _local/shopify/config.json formIds. A Jotform transfer keeps the ids.
  var FORM_EVENTS = {
    '260015038439048': 'lead_contact',
    '260074494311149': 'lead_quote',
    '261826098092161': 'lead_government',
    '253638831939067': 'lead_registration',
    '260705339187159': 'lead_registration'
  };
  var JOTFORM_ORIGIN = /^https:\/\/([a-z0-9-]+\.)*jotform\.com$/i;
  // Native forms: the thank-you view's data-glitch-thanks value picks the event name.
  var MARKER_EVENTS = {
    'quote': 'lead_quote',
    'government': 'lead_government',
    'registration': 'lead_registration',
    'contact': 'lead_contact'
  };
  // The distributor door page, as a relative or absolute href, with an optional query or hash.
  var DOOR_PAGE = /^(https?:\/\/[^\/]+)?\/pages\/become-a-distributor-or-reseller\/?([?#]|$)/i;

  window.dataLayer = window.dataLayer || [];

  function send(name, formId) {
    var params = { page_path: window.location.pathname, form_id: formId || '' };
    window.dataLayer.push(Object.assign({ event: name }, params));
    if (typeof window.gtag === 'function') window.gtag('event', name, params);
  }

  // The pending-send flag: set at a marked submit, taken by the thank-you view in the same tab.
  var PENDING_KEY = 'glitchLeadPending';
  var PENDING_MAX_AGE_MS = 30 * 60 * 1000;

  function rememberPendingSend(kind) {
    try {
      window.sessionStorage.setItem(PENDING_KEY, JSON.stringify({ kind: kind, at: Date.now() }));
    } catch (storageError) {
      // Storage blocked or full: the submit still goes ahead, and this send is not counted.
    }
  }

  // Reads the flag, removes it whatever it held, and says whether it vouches for this view.
  function takePendingSend(kind) {
    var raw;
    try {
      raw = window.sessionStorage.getItem(PENDING_KEY);
      window.sessionStorage.removeItem(PENDING_KEY);
    } catch (storageError) {
      return false; // Storage unreadable: no proof of a send, so no count.
    }
    var pending;
    try {
      pending = JSON.parse(raw);
    } catch (parseError) {
      return false; // A damaged flag proves nothing; it is already removed.
    }
    if (!pending || pending.kind !== kind || typeof pending.at !== 'number') return false;
    var age = Date.now() - pending.at;
    return age >= 0 && age <= PENDING_MAX_AGE_MS;
  }

  function isBodyLink(link) {
    return !(link.closest && link.closest('header, nav, footer'));
  }

  document.addEventListener('click', function (event) {
    var link = event.target && event.target.closest ? event.target.closest('a[href]') : null;
    if (!link) return;
    var href = link.getAttribute('href') || '';
    if (/^tel:/i.test(href)) send('lead_call');
    else if (/^mailto:/i.test(href)) send('lead_email');
    else if (/regist/i.test(href)) send('lead_registration_start');
    else if (DOOR_PAGE.test(href) && isBodyLink(link)) send('lead_registration_start');
  }, true);

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || !form.querySelector) return;
    // A marked form counts on its thank-you view, so its submit fires nothing here; it only
    // leaves a pending flag for that view, and returns before the generic contact path below
    // so it never fires lead_contact either.
    var marker = form.querySelector('input[name="glitch_form"]');
    if (marker) {
      rememberPendingSend(String(marker.value || ''));
      return;
    }
    var type = form.querySelector('input[name="form_type"]');
    if (type && type.value === 'contact') send('lead_contact', 'shopify-contact');
  }, true);

  window.addEventListener('message', function (event) {
    if (!JOTFORM_ORIGIN.test(event.origin || '')) return;
    var data = event.data;
    if (typeof data === 'string') {
      if (data.indexOf('submission-completed') === -1) return;
      try { data = JSON.parse(data); } catch (e) { return; }
    }
    if (!data || data.action !== 'submission-completed') return;
    var formId = String(data.formID || '');
    send(FORM_EVENTS[formId] || 'lead_form', formId);
    // F9: release the custom.css height floor, so the short thank-you screen does not sit above blank space.
    if (/^\d+$/.test(formId)) {
      var frames = document.querySelectorAll('iframe[id^="' + formId + '"]');
      for (var i = 0; i < frames.length; i++) frames[i].style.minHeight = '0px';
    }
  });

  // A reload or a back/forward visit shows a thank-you view that was already counted.
  // Navigation Timing first, then the older performance.navigation (1 reload, 2 back/forward).
  // With neither API, or no performance object at all, the load counts.
  function isRepeatVisit() {
    var perf = window.performance;
    if (!perf) return false;
    if (typeof perf.getEntriesByType === 'function') {
      var entries = perf.getEntriesByType('navigation');
      var entry = entries && entries.length ? entries[0] : null;
      if (entry && typeof entry.type === 'string') {
        return entry.type === 'reload' || entry.type === 'back_forward';
      }
    }
    if (perf.navigation && typeof perf.navigation.type === 'number') {
      return perf.navigation.type === 1 || perf.navigation.type === 2;
    }
    return false;
  }

  // GA4 counts the gtag call, not the bare dataLayer object, and Shopify defines gtag just
  // after DOMContentLoaded starts, which is after this deferred script runs. So wait for it:
  // check every 100 ms, and after 10 seconds send anyway (dataLayer only). One send either way.
  var GTAG_CHECK_MS = 100;
  var GTAG_GIVE_UP_MS = 10000;

  function sendWhenTagReady(name, formId) {
    if (typeof window.gtag === 'function') {
      send(name, formId);
      return;
    }
    var waited = 0;
    var timer = window.setInterval(function () {
      waited += GTAG_CHECK_MS;
      if (typeof window.gtag !== 'function' && waited < GTAG_GIVE_UP_MS) return;
      window.clearInterval(timer);
      send(name, formId);
    }, GTAG_CHECK_MS);
  }

  function countThanksView() {
    var line = document.querySelector('[data-glitch-thanks]');
    if (!line || isRepeatVisit()) return;
    var kind = String(line.getAttribute('data-glitch-thanks') || '');
    if (!takePendingSend(kind)) return;
    var name = Object.prototype.hasOwnProperty.call(MARKER_EVENTS, kind) ? MARKER_EVENTS[kind] : 'lead_form';
    sendWhenTagReady(name, kind);
  }

  // Loaded with defer, so the document is parsed by now; wait for it if it is not.
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', countThanksView);
  else countThanksView();
})();
